import { api } from '../lib/api';
import { createWatermarkedImage } from './imageUtils';

export const GPHOTOS_SAVE_INTENT_KEY = 'kinvera.gphotos.saveIntent';

export type GooglePhotosSaveIntent = {
  reason: 'save';
  fileName?: string;
  ts: number;
};

export function markPendingGooglePhotosSave(fileName?: string) {
  try {
    const payload: GooglePhotosSaveIntent = {
      reason: 'save',
      fileName: fileName || undefined,
      ts: Date.now(),
    };
    sessionStorage.setItem(GPHOTOS_SAVE_INTENT_KEY, JSON.stringify(payload));
  } catch {
    /* ignore quota / private mode */
  }
}

export function peekPendingGooglePhotosSave(): GooglePhotosSaveIntent | null {
  try {
    const raw = sessionStorage.getItem(GPHOTOS_SAVE_INTENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as GooglePhotosSaveIntent;
    if (!parsed || parsed.reason !== 'save') return null;
    // Ignore stale intents older than 30 minutes
    if (Date.now() - (parsed.ts || 0) > 30 * 60 * 1000) {
      sessionStorage.removeItem(GPHOTOS_SAVE_INTENT_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearPendingGooglePhotosSave() {
  try {
    sessionStorage.removeItem(GPHOTOS_SAVE_INTENT_KEY);
  } catch {
    /* ignore */
  }
}

/** Map API / network errors to user-facing copy (never expose tokens). */
export function friendlyGooglePhotosSaveError(err: unknown): {
  kind: 'connect' | 'expired' | 'generic';
  message: string;
} {
  const raw =
    err instanceof Error
      ? err.message
      : typeof err === 'string'
        ? err
        : 'Save to Google Photos failed';
  const lower = raw.toLowerCase();

  if (
    lower.includes('not connected') ||
    lower.includes('authorize first') ||
    lower.includes('authorize')
  ) {
    return {
      kind: 'connect',
      message: 'Connect Google Photos to save this restoration.',
    };
  }
  if (lower.includes('expired') || lower.includes('reconnect') || lower.includes('invalid_grant')) {
    return {
      kind: 'expired',
      message: 'Your Google Photos connection expired. Reconnect to continue.',
    };
  }
  console.error('[Kinvera] Google Photos save failed:', err);
  return {
    kind: 'generic',
    message:
      "Kinvera couldn't save this photo to Google Photos. Your restoration is still safe — try again.",
  };
}

/**
 * Derive export bytes (optionally watermarked) and upload via existing API.
 * Does not mutate the master RestorationVersion imageUrl.
 */
export async function saveRestorationToGooglePhotos(options: {
  imageUrl: string;
  fileName?: string;
  watermarkExportsEnabled?: boolean;
  token?: string | null;
}): Promise<void> {
  const { imageUrl, fileName, watermarkExportsEnabled = true, token } = options;
  const exportUrl =
    watermarkExportsEnabled && imageUrl
      ? await createWatermarkedImage(imageUrl)
      : imageUrl;

  const match = exportUrl.match(/^data:([^;]+);base64,(.+)$/);
  if (!match) {
    throw new Error('Save-back requires an in-session restored image.');
  }

  await api.googlePhotosSave(
    {
      mimeType: match[1],
      base64Data: match[2],
      fileName: fileName || 'kinvera-restored.jpg',
      description: 'Restored with Kinvera',
    },
    token
  );
}
